// projectRules.tsx — Sprint 12. Filter / summary helpers (pure) +
// presentation helpers that return AntD Tags (hence .tsx). Kept in
// one file because the tag renderers are tiny and used both by the
// table column defs and the View modal — splitting would force two
// imports for the same per-status data.
import React from 'react';
import { Tag } from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  ProjectOutlined, WarningOutlined,
} from '@ant-design/icons';
import type {
  Project, ProjectPriority, ProjectSearchFilters, ProjectStatus, ProjectSummary,
} from './projectTypes';

// ─── Pure helpers ────────────────────────────────────────────────────────────

/** Distinct startDate years across the project list, sorted descending. */
export function getAvailableYears(projects: Project[]): number[] {
  const years = new Set<number>();
  projects.forEach(p => years.add(new Date(p.startDate).getFullYear()));
  return Array.from(years).sort((a, b) => b - a);
}

/** Apply the page's filter bag to the project list. Filter values are
 *  ANDed; missing filter values mean "any". */
export function filterProjects(projects: Project[], f: ProjectSearchFilters): Project[] {
  let out = [...projects];

  if (f.searchTerm) {
    const term = f.searchTerm.toLowerCase();
    out = out.filter(p =>
      p.name.toLowerCase().includes(term) ||
      p.description.toLowerCase().includes(term) ||
      p.responsiblePerson.toLowerCase().includes(term),
    );
  }
  if (f.year) {
    out = out.filter(p => new Date(p.startDate).getFullYear() === f.year);
  }
  if (f.status)   out = out.filter(p => p.status   === f.status);
  if (f.category) out = out.filter(p => p.category === f.category);
  if (f.priority) out = out.filter(p => p.priority === f.priority);
  if (f.dateRange) {
    const [start, end] = f.dateRange;
    out = out.filter(p =>
      new Date(p.startDate) >= start &&
      new Date(p.endDate)   <= end,
    );
  }
  return out;
}

/** Roll up totals + counts. Active = PLANNING + IN_PROGRESS. */
export function summarizeProjects(filtered: Project[]): ProjectSummary {
  const active    = filtered.filter(p => p.status === 'IN_PROGRESS' || p.status === 'PLANNING');
  const completed = filtered.filter(p => p.status === 'COMPLETED');
  const totalBudget     = filtered.reduce((s, p) => s + p.budget, 0);
  const totalActualCost = filtered.reduce((s, p) => s + (p.actualCost || 0), 0);
  return {
    totalProjects:     filtered.length,
    activeProjects:    active.length,
    completedProjects: completed.length,
    totalBudget,
    totalActualCost,
    budgetVariance: totalBudget - totalActualCost,
  };
}

/** % of budget consumed, capped at 100 for the progress bar. */
export function calculateBudgetProgress(budget: number, actualCost?: number): number {
  if (!actualCost) return 0;
  return Math.min((actualCost / budget) * 100, 100);
}

// ─── Tag renderers ───────────────────────────────────────────────────────────

const STATUS_TAG_CONFIG: Record<ProjectStatus, { color: string; text: string; icon: React.ReactNode }> = {
  PLANNING:    { color: 'blue',   text: 'PLANNING',    icon: <ClockCircleOutlined /> },
  IN_PROGRESS: { color: 'orange', text: 'IN PROGRESS', icon: <ProjectOutlined /> },
  COMPLETED:   { color: 'green',  text: 'COMPLETED',   icon: <CheckCircleOutlined /> },
  ON_HOLD:     { color: 'purple', text: 'ON HOLD',     icon: <WarningOutlined /> },
  CANCELLED:   { color: 'red',    text: 'CANCELLED',   icon: <ExclamationCircleOutlined /> },
};

const PRIORITY_TAG_CONFIG: Record<ProjectPriority, { color: string; text: string }> = {
  LOW:    { color: 'default', text: 'LOW'    },
  MEDIUM: { color: 'blue',    text: 'MEDIUM' },
  HIGH:   { color: 'orange',  text: 'HIGH'   },
  URGENT: { color: 'red',     text: 'URGENT' },
};

export function getStatusTag(status: ProjectStatus): React.ReactElement {
  const c = STATUS_TAG_CONFIG[status];
  return <Tag color={c.color} icon={c.icon}>{c.text}</Tag>;
}

export function getPriorityTag(priority: ProjectPriority): React.ReactElement {
  const c = PRIORITY_TAG_CONFIG[priority];
  return <Tag color={c.color}>{c.text}</Tag>;
}
